import React, { useState, useEffect } from "react";
import { api } from "../../lib/api";
import { useLanguage, useT } from "../../lib/LanguageContext";
import {
  X,
  Send,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Mail,
  HelpCircle,
  Shield,
  CreditCard,
  Wrench,
  Sparkles,
  Users,
} from "lucide-react";

const SUPPORT_EMAIL = "hey@solunary.health";

const CATEGORY_OPTIONS = [
  { value: "general", labelKey: "support.catGeneral", fallback: "General Inquiry", icon: HelpCircle },
  { value: "billing", labelKey: "support.catBilling", fallback: "Billing & Subscriptions", icon: CreditCard },
  { value: "technical", labelKey: "support.catTechnical", fallback: "Technical & Platform Help", icon: Wrench },
  { value: "legal", labelKey: "support.catLegal", fallback: "Legal, Privacy & Compliance", icon: Shield },
  { value: "feedback", labelKey: "support.catFeedback", fallback: "Feedback & Feature Requests", icon: Sparkles },
  { value: "partnership", labelKey: "support.catPartnership", fallback: "Partnership & Press", icon: Users },
];

export default function SupportTicketModal({
  isOpen,
  onClose,
  defaultCategory = "general",
  defaultSubject = "",
}) {
  const { lang } = useLanguage();
  const t = useT("saas");

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    category: defaultCategory,
    subject: defaultSubject,
    message: "",
  });

  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [ticketResult, setTicketResult] = useState(null);

  // Sync category/subject if modal is reopened with new props
  useEffect(() => {
    if (isOpen) {
      setFormData((prev) => ({
        ...prev,
        category: defaultCategory || "general",
        subject: defaultSubject || prev.subject,
      }));
      setError(null);
      setTicketResult(null);
    }
  }, [isOpen, defaultCategory, defaultSubject]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (error) setError(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      setError(t("support.errorName") || "Please enter your name.");
      return;
    }
    if (!formData.email.trim() || !formData.email.includes("@")) {
      setError(t("support.errorEmail") || "Please enter a valid email address.");
      return;
    }
    if (!formData.subject.trim() || formData.subject.length < 2) {
      setError(t("support.errorSubject") || "Please enter a subject for your inquiry.");
      return;
    }
    if (!formData.message.trim() || formData.message.length < 5) {
      setError(t("support.errorMessage") || "Please describe your request in at least 5 characters.");
      return;
    }

    setSubmitting(true);
    setError(null);

    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim().toLowerCase(),
      category: formData.category,
      subject: formData.subject.trim(),
      message: formData.message.trim(),
      source_url: window.location.pathname,
      locale: lang || "en",
    };

    try {
      const resp = await api.post("/support/ticket", payload);

      setTicketResult(resp.data);
      setFormData({
        name: "",
        email: "",
        category: "general",
        subject: "",
        message: "",
      });
    } catch (err) {
      // The server's `detail` is a code or an English diagnostic (a rate-limit refusal
      // answers "RATE_LIMITED"), never a sentence for the visitor.
      setError(t("support.errorGeneric") || "Failed to submit support ticket. Please write to hey@solunary.health directly.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      data-testid="support-modal"
      className="fixed inset-0 z-50 overflow-y-auto bg-sol-midnight/70 backdrop-blur-sm flex items-center justify-center p-4 sm:p-6"
    >
      <div className="relative bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 shadow-2xl border border-sol-midnight/10 my-8">
        {/* Close Button */}
        <button
          type="button"
          onClick={onClose}
          data-testid="support-modal-close"
          className="absolute top-5 right-5 text-sol-midnight/50 hover:text-sol-midnight transition p-1.5 rounded-full hover:bg-sol-midnight/5"
          aria-label={t("support.close") || "Close"}
        >
          <X className="w-5 h-5" />
        </button>

        {ticketResult ? (
          /* Success Screen */
          <div data-testid="support-success-card" className="text-center py-4">
            <div className="w-16 h-16 bg-emerald-50 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4 border border-emerald-100">
              <CheckCircle2 className="w-8 h-8" />
            </div>

            <h3 className="font-display text-2xl text-sol-midnight mb-2">
              {t("support.successTitle") || "Inquiry Received"}
            </h3>

            <p className="text-sm text-sol-midnight/70 leading-relaxed max-w-md mx-auto mb-6">
              {t("support.successMessage") ||
                "Thank you for contacting Solunary. We have dispatched your inquiry to our support team and sent a confirmation receipt to your email."}
            </p>

            <div className="bg-sol-ivory/60 rounded-2xl p-4 border border-sol-midnight/8 max-w-sm mx-auto mb-6">
              <div className="text-xs uppercase tracking-wider text-sol-midnight/50 mb-1">
                {t("support.ticketReference") || "Ticket Reference"}
              </div>
              <div
                data-testid="support-ticket-number"
                className="font-mono text-lg font-bold text-sol-midnight tracking-wider"
              >
                {ticketResult.ticket_number}
              </div>
            </div>

            <div className="p-3 bg-sol-gold/10 border border-sol-gold/20 rounded-xl text-xs text-sol-midnight/80 mb-6 flex items-center justify-center gap-2">
              <Mail className="w-4 h-4 text-sol-gold shrink-0" />
              <span>
                Direct mailbox:{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="font-semibold underline hover:text-sol-gold">
                  {SUPPORT_EMAIL}
                </a>
              </span>
            </div>

            <div className="flex gap-3 justify-center">
              <button
                type="button"
                onClick={() => setTicketResult(null)}
                className="px-4 py-2.5 rounded-xl border border-sol-midnight/15 text-xs font-semibold text-sol-midnight hover:bg-sol-midnight/5 transition"
              >
                {t("support.anotherInquiry") || "Send another inquiry"}
              </button>
              <button
                type="button"
                onClick={onClose}
                className="px-6 py-2.5 rounded-xl bg-sol-midnight text-sol-ivory text-xs font-semibold hover:bg-sol-midnight/90 transition shadow-sm"
              >
                {t("support.done") || "Done"}
              </button>
            </div>
          </div>
        ) : (
          /* Form Screen */
          <div>
            <div className="mb-6">
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-sol-gold/15 text-sol-midnight font-medium text-xs mb-2">
                <HelpCircle className="w-3.5 h-3.5 text-sol-gold" />
                <span>{t("support.badge") || "Solunary Support"}</span>
              </div>
              <h2 className="font-display text-2xl text-sol-midnight">
                {t("support.formTitle") || "How can we help you?"}
              </h2>
              <p className="text-xs text-sol-midnight/60 mt-1">
                {t("support.formSubtitle") ||
                  "Submit your ticket below or email us directly at"}{" "}
                <a href={`mailto:${SUPPORT_EMAIL}`} className="text-sol-gold font-medium hover:underline">
                  {SUPPORT_EMAIL}
                </a>
              </p>
            </div>

            {error && (
              <div
                data-testid="support-error-banner"
                className="mb-4 p-3 bg-rose-50 border border-rose-200 rounded-xl text-xs text-rose-800 flex items-start gap-2"
              >
                <AlertCircle className="w-4 h-4 text-rose-600 shrink-0 mt-0.5" />
                <span className="leading-relaxed">{error}</span>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-sol-midnight/80 mb-1">
                    {t("support.nameLabel") || "Your Name"} <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="text"
                    name="name"
                    data-testid="support-input-name"
                    required
                    value={formData.name}
                    onChange={handleChange}
                    placeholder="e.g. Marie Laurent"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-sol-midnight/15 text-xs text-sol-midnight focus:outline-none focus:ring-2 focus:ring-sol-gold/50 bg-sol-ivory/20"
                  />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-sol-midnight/80 mb-1">
                    {t("support.emailLabel") || "Your Email"} <span className="text-rose-500">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    data-testid="support-input-email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    placeholder="name@example.com"
                    className="w-full px-3.5 py-2.5 rounded-xl border border-sol-midnight/15 text-xs text-sol-midnight focus:outline-none focus:ring-2 focus:ring-sol-gold/50 bg-sol-ivory/20"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-sol-midnight/80 mb-1">
                  {t("support.categoryLabel") || "Category"}
                </label>
                <select
                  name="category"
                  data-testid="support-select-category"
                  value={formData.category}
                  onChange={handleChange}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-sol-midnight/15 text-xs text-sol-midnight focus:outline-none focus:ring-2 focus:ring-sol-gold/50 bg-sol-ivory/20"
                >
                  {CATEGORY_OPTIONS.map((cat) => (
                    <option key={cat.value} value={cat.value}>
                      {t(cat.labelKey) || cat.fallback}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-xs font-semibold text-sol-midnight/80 mb-1">
                  {t("support.subjectLabel") || "Subject"} <span className="text-rose-500">*</span>
                </label>
                <input
                  type="text"
                  name="subject"
                  data-testid="support-input-subject"
                  required
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder={t("support.subjectPlaceholder") || "Brief summary of your question"}
                  className="w-full px-3.5 py-2.5 rounded-xl border border-sol-midnight/15 text-xs text-sol-midnight focus:outline-none focus:ring-2 focus:ring-sol-gold/50 bg-sol-ivory/20"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-sol-midnight/80 mb-1">
                  {t("support.messageLabel") || "Message"} <span className="text-rose-500">*</span>
                </label>
                <textarea
                  name="message"
                  data-testid="support-input-message"
                  required
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder={
                    t("support.messagePlaceholder") ||
                    "Provide any relevant details so we can assist you efficiently..."
                  }
                  className="w-full px-3.5 py-2.5 rounded-xl border border-sol-midnight/15 text-xs text-sol-midnight focus:outline-none focus:ring-2 focus:ring-sol-gold/50 bg-sol-ivory/20 resize-y"
                />
              </div>

              <div className="pt-2 flex items-center justify-between gap-3">
                <span className="text-[11px] text-sol-midnight/50">
                  Direct inquiries:{" "}
                  <a href={`mailto:${SUPPORT_EMAIL}`} className="underline hover:text-sol-gold">
                    {SUPPORT_EMAIL}
                  </a>
                </span>
                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="support-submit-btn"
                  className="px-6 py-2.5 rounded-xl bg-sol-midnight text-sol-gold font-semibold text-xs hover:bg-sol-midnight/90 disabled:opacity-50 transition inline-flex items-center gap-2 shadow-sm"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      <span>{t("support.submitting") || "Sending..."}</span>
                    </>
                  ) : (
                    <>
                      <Send className="w-3.5 h-3.5" />
                      <span>{t("support.send") || "Send Message"}</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        )}
      </div>
    </div>
  );
}
